"use client";

import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { PlayerAvatar } from "@/components/player-avatar";
import { ApiError } from "@/lib/api-client";
import { reportError } from "@/lib/client-log";
import { fetchPlayerProfile, updatePlayerProfile, type PlayerProfile } from "@/lib/daily-word";
import { useMessages } from "@/lib/i18n/client";
import { PlayerProfileDialog } from "./PlayerProfileDialog";
import messages from "./ranking.i18n";

// Ranking opt-in: al terminar una partida con sesión, una sola vez, se
// muestra cómo se vería la persona en el ranking y decide si sumarse.
export function JoinRankingCard() {
  const t = useMessages(messages);
  const [profile, setProfile] = useState<PlayerProfile | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [justJoined, setJustJoined] = useState(false);

  useEffect(() => {
    fetchPlayerProfile()
      .then(setProfile)
      .catch((err: unknown) =>
        reportError("player-profile-load", err, { httpStatus: err instanceof ApiError ? err.status : undefined }),
      );
  }, []);

  async function answer(joined: boolean) {
    setBusy(true);
    try {
      setProfile(await updatePlayerProfile(joined ? { joined: true } : { promptAnswered: true }));
      setJustJoined(joined);
    } catch (err) {
      reportError("player-profile-answer", err, { httpStatus: err instanceof ApiError ? err.status : undefined });
    } finally {
      setBusy(false);
    }
  }

  if (justJoined) {
    return <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">{t.join.joined}</p>;
  }
  if (!profile || profile.promptAnswered) return null;

  return (
    <div className="w-full rounded-xl bg-primary/10 p-4 text-left">
      <p className="text-sm font-semibold">{t.join.title}</p>
      <p className="mt-1 text-sm text-muted-foreground">{t.join.body}</p>
      <div className="mt-3 flex items-center gap-3 rounded-lg bg-background p-2.5">
        <PlayerAvatar player={profile.card} size={36} />
        <span className="truncate font-semibold">{profile.card.name}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => answer(true)}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {t.join.yes}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setEditing(true)}
          className="inline-flex h-10 items-center justify-center rounded-full border border-border bg-background px-5 text-sm font-semibold hover:bg-muted"
        >
          {t.join.edit}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => answer(false)}
          className="inline-flex h-10 items-center justify-center rounded-full px-4 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          {t.join.no}
        </button>
      </div>
      <PlayerProfileDialog
        open={editing}
        onOpenChange={setEditing}
        profile={profile}
        onSaved={(saved) => {
          setProfile(saved);
          setEditing(false);
          setJustJoined(saved.joined);
        }}
      />
    </div>
  );
}
